
// src/contexts/StockWatchlistContext.js
import React, { createContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';

export const WatchlistContext = createContext();

const API_URL = 'https://1azyop0jo8.execute-api.eu-west-2.amazonaws.com/dev/watchlist';


export function WatchlistProvider({ children }) {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const [symbols, setSymbols] = useState([]);
  const [selectedForComparison, setSelectedForComparison] = useState([]);

  // Fetch watchlist on login
// Replace your existing fetchWatchlist with this:
const fetchWatchlist = useCallback(async () => {
  if (!token) return;
  try {
    const res = await fetch(API_URL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    const data = await res.json();
    const serverSymbols = Array.isArray(data) ? data : [];

    // Merge guest-added symbols (e.g., NVDA) captured pre-login
    let pending = [];
    try { pending = JSON.parse(sessionStorage.getItem('pendingSave') || '[]'); } catch { pending = []; }

    const merged = pending.length
      ? Array.from(new Set([...serverSymbols, ...pending]))
      : serverSymbols;

    // Immediately reflect merged list in UI after redirect
    setSymbols(merged);

    // Persist merge once, then clear pending
    if (pending.length) {
      await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ data: merged })
      });
      sessionStorage.removeItem('pendingSave');
    }
  } catch (err) {
    console.error('Failed to fetch watchlist:', err);
  }
}, [token]);


  useEffect(() => {
    if (auth.isAuthenticated) fetchWatchlist();
  }, [auth.isAuthenticated, fetchWatchlist]);

  // Save to backend
  const saveWatchlist = async (updatedSymbols) => {
    if (!token) return;
    try {
      await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ data: updatedSymbols })
      });
    } catch (err) {
      console.error('Failed to save watchlist:', err);
    }
  };

  const add = (sym) => {
    setSymbols(prev => {
      const updated = prev.includes(sym) ? prev : [...prev, sym];
      saveWatchlist(updated);
      return updated;
    });
  };

  const remove = (sym) => {
    setSymbols(prev => {
      const updated = prev.filter(s => s !== sym);
      saveWatchlist(updated);
      return updated;
    });
  };

  const addMany = async (symbolsToAdd) => {
    if (!token) return;
    try {
      const res = await fetch(API_URL, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const serverSymbols = await res.json();
      const merged = Array.from(new Set([...(Array.isArray(serverSymbols) ? serverSymbols : []), ...symbolsToAdd]));
      setSymbols(merged);
      await saveWatchlist(merged);
    } catch (err) {
      console.error('Failed to merge watchlist:', err);
    }
  };


  const toggleSelection = (sym) => {
    setSelectedForComparison(prev =>
      prev.includes(sym) ? prev.filter(s => s !== sym) : [...prev, sym]
    );
  };

  const clearSelection = () => setSelectedForComparison([]);

  return (
    <WatchlistContext.Provider value={{
      symbols,
      add,
      addMany,
      remove,
      selectedForComparison,
      toggleSelection,
      clearSelection
    }}>
      {children}
    </WatchlistContext.Provider>
  );
}


